export const auth = {
  backToApp: "Back to Lyder Talk",
  passwordStrength: {
    label: "Strength",
    weak: "Weak",
    fair: "Fair",
    strong: "Strong",
    excellent: "Excellent",
  },
  passwordRules: {
    characters: "8+ characters",
    number: "a number",
    capital: "a capital letter",
  },
  signin: {
    title: "Welcome back",
    subtitle: "Sign in to keep your streak going.",
    submit: "Sign in",
    submitting: "Signing in…",
    noAccount: "New to Lyder Talk?",
    createAccount: "Create an account",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "Your password",
    forgotPassword: "Forgot password?",
    showPassword: "Show password",
    hidePassword: "Hide password",
    errors: {
      invalidCredentials: "That email and password don't match.",
      required: "Enter your email and password.",
      unexpected: "We couldn't sign you in. Please try again.",
    },
  },
  signup: {
    title: "Create your account",
    subtitle: "Start building your speaking practice today.",
    submit: "Create account",
    submitting: "Creating account…",
    haveAccount: "Already have an account?",
    signInInstead: "Sign in",
    displayNameLabel: "Display name",
    displayNamePlaceholder: "How should we call you?",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    acceptTerms: "I agree to the Terms of Service and Privacy Policy",
    submitDisabledTerms: "Accept the terms to continue",
    showPassword: "Show password",
    hidePassword: "Hide password",
    errors: {
      emailInUse: "An account with that email already exists.",
      weakPassword: "Use at least 8 characters.",
      terms: "Please accept the terms to continue.",
      unexpected: "We couldn't create your account. Please try again.",
    },
  },
  forgotPassword: {
    title: "Reset your password",
    subtitle: "We'll email you a link to choose a new one.",
    submit: "Send reset link",
    submitting: "Sending…",
    backToSignIn: "Back to sign in",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    sent: "Check your inbox",
    sentDescription: "If an account exists for that email, a reset link is on its way.",
  },
  resetPassword: {
    title: "Choose a new password",
    submit: "Update password",
    submitting: "Updating…",
    passwordLabel: "New password",
    passwordPlaceholder: "At least 8 characters",
    confirmLabel: "Confirm password",
    confirmPlaceholder: "Repeat your new password",
    mismatch: "Those passwords don't match.",
    showPassword: "Show password",
    hidePassword: "Hide password",
    success: "Password updated",
    successDescription: "You can sign in with your new password now.",
  },
  confirmEmail: {
    title: "Confirm your email",
    description: "We sent a confirmation link to {{email}}. Open it to finish setting up your account.",
    resend: "Resend confirmation",
    resending: "Sending…",
    resent: "Confirmation sent",
    changeEmail: "Use a different email",
  },
  invite: {
    title: "You're invited",
    subtitle: "Join {{groupName}} on Lyder Talk.",
    accept: "Accept invitation",
    decline: "Not now",
  },
  /**
   * Copy for the shared `SigninForm` / `SignupForm` / `ForgotPasswordForm` /
   * `ResetPasswordForm` in `@lyder-talk/ui`.
   *
   * Those live in a package shared with the admin panel, so they stay
   * locale-agnostic and take their strings from a `copy` prop instead. This
   * namespace is the app's side of that contract.
   */
  forms: {
    signin: {
      showPassword: "Show password",
      hidePassword: "Hide password",
      title: "Welcome back",
      description: "Pick up your streak where you left it.",
      emailLabel: "Email address",
      emailPlaceholder: "you@example.com",
      passwordLabel: "Password",
      forgotPassword: "Forgot password?",
      submitting: "Signing you in…",
      submit: "Sign in",
      continueWithGoogle: "Continue with Google",
      newHere: "New here?",
      createAccount: "Create an account",
      genericError: "An error occurred. Please try again.",
    },
    signup: {
      showPassword: "Show password",
      hidePassword: "Hide password",
      successTitle: "You're all set",
      successDescription: "Your desk is ready. Head in and build your first topic.",
      successTitleConfirmEmail: "Confirm your email",
      successDescriptionConfirmEmail: "We sent a confirmation link to",
      steps: [
        "Sign in with your new account",
        "Generate your first speaking topic",
        "Save words you meet while you talk",
      ],
      successActionConfirmEmail: "Go to sign in",
      successAction: "Start learning",
      title: "Create your account",
      description: "Free to start. Your progress and saved words stay with you.",
      displayNameLabel: "Display name",
      displayNamePlaceholder: "How should we greet you?",
      emailLabel: "Email address",
      passwordLabel: "Password",
      confirmPasswordLabel: "Confirm password",
      matchHint: "Both entries must match.",
      creating: "Creating your account…",
      submit: "Create account",
      googleLabel: "Sign up with Google",
      passwordTooShort: "Password must be at least 8 characters.",
      passwordsMismatch: "Passwords do not match.",
      unexpectedError: "An unexpected error occurred. Please try again.",
      haveAccount: "Already have an account?",
      signInInstead: "Sign in",
    },
    forgot: {
      title: "Reset your password",
      description: "Enter your email and we'll send you a link to choose a new one.",
      emailLabel: "Email address",
      sending: "Sending the link…",
      submit: "Send reset link",
      backToSignIn: "Back to sign in",
      sentTitle: "Check your inbox",
      sentDescription: "If an account exists for {{email}}, a reset link is on its way — it expires in one hour.",
      tryAnotherEmail: "Try another email",
      sendFailed: "Failed to send reset email. Please try again.",
    },
    reset: {
      showPassword: "Show password",
      hidePassword: "Hide password",
      title: "Create a new password",
      description: "Choose something you will remember but nobody would guess.",
      passwordLabel: "New password",
      confirmPasswordLabel: "Confirm new password",
      matchHint: "Both entries must match.",
      submit: "Reset password",
      submitting: "Updating password…",
      backToSignIn: "Back to sign in",
      tooShort: "Password must be at least 8 characters long.",
      passwordsMismatch: "Passwords do not match.",
      failed: "Failed to reset password. Please try again.",
      successTitle: "Password updated",
      successDescription: "Your password has been reset. Sign in with your new password.",
      missingToken: "This password reset link is missing its token. Request a new one below.",
    },
    /** The marketing panel beside the auth form (`AuthShowcase`). */
    showcase: {
      eyebrow: "Lyder Talk",
      headlineLineOne: "Learn a language by",
      headlineLineTwo: "actually speaking it",
      description:
        "Ten focused minutes a day. Real conversations, your own words, and a record of how far you've come.",
      benefits: [
        "An AI tutor that remembers your level",
        "Native-like pronunciation on every reply",
        "Topics written fresh around what you're learning",
        "Accuracy and streak, tracked week by week",
      ],
    },
    legal: {
      termsPrefix: "By continuing you agree to our",
      termsLink: "Terms",
      andPrefix: "and",
      privacyLink: "Privacy Policy",
      orContinueWith: "or continue with",
    },
    toasts: {
      welcomeBack: "Welcome back!",
      welcome: "Welcome!",
      error: "Error",
      accountExists: "Account already exists",
      accountExistsDescription: "You already have an account! Please sign in.",
      googleFailed: "Failed to sign in with Google",
    },
  },
  callback: {
    signingIn: "Signing you in…",
    failed: "We couldn't complete sign-in.",
  },
  /**
   * The sign-in gate (`AuthRequiredModal`), which answers "why do you want my
   * account?" in the terms of whatever the visitor was about to do.
   *
   * Only the gate's own furniture lives here. Its sign-in, sign-up and
   * forgot-password screens are the shared `forms` ones below — the same
   * components `/auth/*` renders — so their copy is shared, not mirrored.
   */
  modal: {
    toasts: {
      checkEmail: "Check your email",
      checkEmailDescription: "Please confirm your email to activate your account.",
      error: "Error",
    },
    /**
     * The sign-in gate (`AuthRequiredModal`). Reached mid-task by an anonymous
     * visitor, so every string answers "why do you want my account?" in the
     * terms of the thing they were about to do — never a generic sign-in pitch.
     *
     * The sign-in / sign-up / forgot-password copy lives in `forms` below, and
     * is shared with the standalone `/auth/*` pages.
     */
    required: {
      title: "Sign in to continue",
      description:
        "Create a free account to save your work and pick up right where you left off.",
      guardDescription:
        "This page is saved to your account. Sign in or create one to open it.",
      filterDescription:
        "These are your own lists. Sign in or create an account to see them.",
      signIn: "Sign in",
      signUp: "Create a free account",
      footnote: "Free to start · no card required",
      formDescription: "Sign in or create an account to continue.",
      benefits: {
        save: "Save and reopen your recordings",
        feedback: "Get AI feedback on your speaking",
        progress: "Track your progress across sessions",
        history: "Keep a history you can revisit",
      },
      noteTitle: "Sign in to keep your note",
      noteDescription:
        "Notes are saved to your account, so they stay with your vocabulary and your sessions.",
      generateTitle: "Sign in to generate a topic",
      generateDescription:
        "Generated questions and vocabulary are saved to your account.",
    },
  },
} as const;